import type { QueryClient } from "@tanstack/react-query";
import type { AnyRouter, NavigateFn } from "@tanstack/react-router";
import { AxiosError } from "axios";
import type { UseFormReturn } from "react-hook-form";
import { listImagesApiImagesGetQueryKey } from "../generated/api/@tanstack/react-query.gen";
import type { HydratedImage, ParameterGroup } from "../generated/api/types.gen";
import { trackEvent } from "./analytics";
import { getErrorMessage } from "./api-client";
import { setParameterResyncPending } from "./parameter-resync";
import {
  invalidateGallerySessions,
  invalidateIterationUsage,
  invalidateProjectAssets,
} from "./queries";
import {
  getRegenerationStream,
  processRegenerationStream,
} from "./regeneration-stream";

export type RegenerationFormValues = {
  parameter_groups: ParameterGroup[];
  prompt: string;
  referenceImages: string[];
};

export interface RegenerationInput {
  refinementImage: HydratedImage | null;
  promptDirty: boolean;
  promptText: string;
  originalPrompt: string;
  sessionId: string | undefined;
}

export interface RegenerationFormState {
  formMethods: UseFormReturn<RegenerationFormValues>;
  onImageUpdate: (image: HydratedImage) => void;
  setIsRegenerating: (v: boolean) => void;
}

export interface RegenerationDeps {
  queryClient: QueryClient;
  router: AnyRouter;
  navigate: NavigateFn;
  redirectToSignIn: () => void;
}

async function handleRegenerationSuccess({
  newImage,
  requestId,
  input,
  form,
  deps,
}: {
  newImage: HydratedImage;
  requestId: string;
  input: RegenerationInput;
  form: RegenerationFormState;
  deps: RegenerationDeps;
}): Promise<void> {
  const { parameter_groups } = form.formMethods.getValues();
  form.formMethods.reset({
    parameter_groups: parameter_groups ?? [],
    prompt: "",
    referenceImages: [],
  });
  form.setIsRegenerating(false);
  await invalidateGallerySessions(deps.queryClient, deps.router);
  // A refinement can attach an asset to the design, which the project sidebar
  // shows as a derived-used asset. Refresh it so the new asset appears when the
  // user returns to the gallery instead of after a hard refresh.
  await invalidateProjectAssets(deps.queryClient);
  // This refinement spent an iteration; re-read the allowance so the header
  // pill and the out-of-iterations overlay reflect it immediately.
  await invalidateIterationUsage(deps.queryClient);
  if (input.sessionId) {
    await deps.queryClient.invalidateQueries({
      queryKey: listImagesApiImagesGetQueryKey({
        query: { session_id: input.sessionId },
      }),
    });
  }
  deps.navigate({
    to: "/refine",
    search: {
      sessionId: input.sessionId || "",
      requestId,
      imageId: newImage.id,
    },
  });
}

async function handleRegenerationError(
  error: unknown,
  form: RegenerationFormState,
  deps: RegenerationDeps,
  flow: string,
): Promise<void> {
  form.setIsRegenerating(false);
  if (error instanceof AxiosError && error.response?.status === 401) {
    deps.redirectToSignIn();
    return;
  }
  // A 403 here is almost always a spent allowance the cache hadn't caught
  // up with (e.g. iterations spent in another tab). Re-read it so the
  // out-of-iterations overlay appears instead of leaving a bare alert.
  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    error.status === 403
  ) {
    await invalidateIterationUsage(deps.queryClient);
    trackEvent("iteration_limit_hit", { flow });
  }
  alert(getErrorMessage(error));
}

/** Run the stream and hand the first image to handleRegenerationSuccess.
 *  Writes the id of the image whose parameters need a resync into
 *  `setResyncImageId` as soon as it arrives (the stream may stay open for the
 *  parameters follow-up, so this can't be a return value). */
async function runRegenerationStream({
  input,
  form,
  deps,
  requestId,
  originalImageId,
  currentParams,
  usePromptRefinement,
  setResyncImageId,
}: {
  input: RegenerationInput;
  form: RegenerationFormState;
  deps: RegenerationDeps;
  requestId: string;
  originalImageId: string;
  currentParams: {
    parameter_groups: ParameterGroup[];
    referenceImages: string[];
  };
  usePromptRefinement: boolean;
  setResyncImageId: (id: string) => void;
}): Promise<void> {
  const stream = getRegenerationStream({
    usePromptRefinement,
    requestId,
    originalImageId,
    promptDirty: input.promptDirty,
    promptText: input.promptText,
    originalPrompt: input.originalPrompt,
    currentParams,
  });
  const newImage = await processRegenerationStream({
    stream,
    originalImageId,
    refinementImage: input.refinementImage,
    onImageUpdate: form.onImageUpdate,
    reset: (values) =>
      form.formMethods.reset({
        // Keep prompt/referenceImages during streaming updates so they can be
        // retried if the stream fails; they are cleared only on success.
        ...form.formMethods.getValues(),
        parameter_groups: values.parameter_groups,
      }),
    // Navigate as soon as the image arrives; the stream stays open for the
    // parameters_updated follow-up, which lands in the query cache via
    // onImageUpdate and reseeds the form through useRefineForm.
    onImageReady: (image) => {
      setResyncImageId(image.id);
      setParameterResyncPending(deps.queryClient, image.id, true);
      return handleRegenerationSuccess({
        newImage: image,
        requestId,
        input,
        form,
        deps,
      });
    },
  });
  if (!newImage) {
    throw new Error(
      "Regeneration stream completed without producing an image.",
    );
  }
}

export async function regenerateImages({
  input,
  form,
  deps,
}: {
  input: RegenerationInput;
  form: RegenerationFormState;
  deps: RegenerationDeps;
}): Promise<void> {
  const originalImageId = input.refinementImage?.id;
  if (!originalImageId) return;

  const { parameter_groups, referenceImages } = form.formMethods.getValues();
  const currentParams = {
    parameter_groups: parameter_groups ?? [],
    referenceImages: referenceImages ?? [],
  };

  form.setIsRegenerating(true);
  const hasParameters = currentParams.parameter_groups.length > 0;
  const usePromptRefinement =
    (input.promptDirty && input.promptText.trim().length > 0) || !hasParameters;
  const requestId = crypto.randomUUID();

  let resyncImageId: string | null = null;
  try {
    await runRegenerationStream({
      input,
      form,
      deps,
      requestId,
      originalImageId,
      currentParams,
      usePromptRefinement,
      setResyncImageId: (id) => {
        resyncImageId = id;
      },
    });
  } catch (error: unknown) {
    await handleRegenerationError(
      error,
      form,
      deps,
      usePromptRefinement ? "prompt_refine" : "attribute_refine",
    );
  } finally {
    // The params event (or the resync failing silently) precedes stream end,
    // so clearing here covers both the success and no-event cases.
    if (resyncImageId) {
      setParameterResyncPending(deps.queryClient, resyncImageId, false);
    }
  }
}
